import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  LayoutDashboard, 
  FileText, 
  Upload, 
  Users, 
  UserCog, 
  Settings,
  LogOut,
  Clock,
  Share2,
  Palette,
  FileBarChart,
  Building2,
  CreditCard,
  MessageSquare,
  MessageCircle,
  User,
  Briefcase,
  Shield,
  Image,
  HelpCircle,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

export const Sidebar = ({ role = 'client', onLogout }) => {
  const location = useLocation();
  
  const getMenuItems = () => {
    switch (role) {
      case 'client':
        // Unified client menu with all filing types
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
          { icon: User, label: 'Individual Tax Filing', path: '/filing/start' },
          { icon: Briefcase, label: 'Business Tax Filing', path: '/business/dashboard' },
          { icon: Building2, label: 'LLC Formation', path: '/llc/dashboard' },
          { icon: Clock, label: 'Filing Status', path: '/filing-status' },
          { icon: CreditCard, label: 'Payments', path: '/payments' },
          { icon: Share2, label: 'Referrals', path: '/referrals' },
          { icon: Settings, label: 'Profile', path: '/profile' },
        ];
      case 'business':
        return [
          { icon: LayoutDashboard, label: 'Business Dashboard', path: '/business/dashboard' },
          { icon: FileText, label: 'Business Filing', path: '/business/filing/start' },
          { icon: Clock, label: 'Filing Status', path: '/business/filing-status' },
          { icon: Settings, label: 'Profile', path: '/profile' },
        ];
      case 'admin':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
          { icon: Users, label: 'Users', path: '/admin/users' },
          { icon: UserCog, label: 'Agents', path: '/admin/agents' },
          { icon: MessageCircle, label: 'Live Chat', path: '/admin/chat' },
          { icon: MessageSquare, label: 'Consultations', path: '/admin/consultations' },
          { icon: FileBarChart, label: 'Filings', path: '/admin/filings' },
          { icon: CreditCard, label: 'Payments', path: '/admin/payments' },
          { icon: Share2, label: 'Referrals', path: '/admin/referrals' },
          { icon: TrendingUp, label: 'Reports', path: '/admin/reports' },
          { icon: Settings, label: 'Tax Pricing', path: '/admin/pricing' },
          { icon: Building2, label: 'LLC Pricing', path: '/admin/llc-pricing' },
          { icon: Image, label: 'Dashboard Flyer', path: '/admin/flyer' },
          { icon: HelpCircle, label: 'FAQ Management', path: '/admin/faq' },
          { icon: Shield, label: 'SOC 2 Compliance', path: '/admin/compliance' },
          { icon: Settings, label: 'Settings', path: '/admin/settings' },
          { icon: User, label: 'Profile', path: '/admin/profile' },
        ];
      case 'agent':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/agent/dashboard' },
          { icon: Users, label: 'My Clients', path: '/agent/clients' },
          { icon: Building2, label: 'LLC Formations', path: '/agent/llc' },
          { icon: MessageCircle, label: 'Live Chat', path: '/agent/chat' },
          { icon: User, label: 'Profile', path: '/agent/profile' },
        ];
      case 'agency':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/agency/dashboard' },
          { icon: Palette, label: 'Theme Builder', path: '/agency/theme' },
          { icon: Users, label: 'Users', path: '/agency/users' },
          { icon: FileBarChart, label: 'Filings', path: '/agency/filings' },
          { icon: MessageCircle, label: 'Live Chat', path: '/agency/chat' },
          { icon: Settings, label: 'Settings', path: '/agency/settings' },
        ];
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();

  return (
    <div className="flex h-screen w-64 flex-col border-r bg-card shadow-material-md">
      {/* Logo */}
      <NavLink 
        to={role === 'admin' ? '/admin/dashboard' : role === 'agent' ? '/agent/dashboard' : role === 'agency' ? '/agency/dashboard' : role === 'business' ? '/business/dashboard' : '/dashboard'} 
        className="flex h-20 items-center border-b px-6 transition-transform cursor-pointer hover:scale-105 active:scale-95 focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Kosha Tax Logo" className="h-14 w-14 object-contain" />
          <div className="flex flex-col">
            <span className="text-xl font-bold text-primary">Kosha Tax</span>
            <span className="text-[10px] text-muted-foreground -mt-1">Precision in Every Return.</span>
          </div>
        </div>
      </NavLink>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-material-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="border-t p-4">
        <Separator className="mb-4" />
        <Button
          variant="ghost"
          className="w-full justify-start gap-3"
          onClick={onLogout}
        >
          <LogOut className="h-5 w-5" />
          <span>Logout</span>
        </Button>
      </div>
    </div>
  );
};
